import isEmpty from 'lodash/isEmpty';
import omitBy from 'lodash/omitBy';
import { GET_MEMBER_CHANNELS_FULFILLED } from 'store/app/entities/channels/action';
import {
  ADD_MESSAGE,
  CONFIRM_ACKNOWLEDGE_MESSAGE_FULFILLED,
  DESTROY_MESSAGE_FULFILLED,
  GENERATE_MESSAGE_PREVIEW_FULFILLED,
  LOAD_MESSAGES_FULFILLED,
  MARK_AS_READ_MESSAGES,
  ON_READ_MESSAGE,
  PERMISSIONS_MESSAGE_FULFILLED,
  PERMISSIONS_MESSAGE_PENDING,
  PERMISSIONS_MESSAGE_REJECTED,
  READ_MESSAGES_FULFILLED,
  SEND_MESSAGE_FULFILLED,
  SEND_MESSAGE_PENDING,
  SEND_MESSAGE_REJECTED,
  UPDATE_MESSAGE_FULFILLED,
} from 'store/app/entities/messages/action';
import uniqid from 'uniqid';
import arrayToObject from 'utils/arrayToObject';
import generatePath from 'utils/generatePath';
import merge from 'utils/merge';

/**
 * WebSocket `newMessage` may send a partial message. Spreading `patch` over the existing
 * entity without this would drop keys missing from the patch (e.g. `acknowledges` with
 * `signedDocUrl` / `logUrl` needed for the acknowledgment read modal).
 */
const mergeMessagePatch = (existing, patch) => ({
  ...existing,
  ...Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined)),
});

const createAttachment = a => {
  const mimeType = a.mimeType || a.mime_type || a.meta?.mimeType || a.meta?.mime_type || undefined;
  return {
    _id: a._id,
    user_id: a.user?._id || a.user,
    httpLink: generatePath(a.httpLink),
    meta: a.meta,
    name: a.name,
    size: a.size,
    type: a.type,
    mimeType,
    createdAt: a.createdAt,
  };
};

const createMessage = m => ({
  _id: m._id,
  text: m.text,
  mentions: m.mentions,
  title: m.title,
  acknowledges: m.acknowledges,
  attachments: m.attachments?.map(a => createAttachment(a)) || [],
  channel_id: m.channel_id || m.channel,
  company_id: m.company_id || m.company,
  previews: {},
  createdAt: m.createdAt,
  employee_id: m.employee_id || m.employee?._id || m.employee,
  from: m.from,
  user_id: m.user_id || m.from?._id,
  isDirect: m.isDirect,
  isDeleted: !!m.deletedAt,
  isDeletedBy: m.isDeletedBy,
  isSuperAdmin: m?.from?.role === 2,
  isDraft: m.isDraft,
  isEdited: m.isEdited,
  softDeletedAt: m.softDeletedAt,
  isMandatory: m.isMandatory,
  isRead: m.isRead,
  isReadByMe: m.isReadByMe,
  links: m.links,
  temp: m.temp || false,
  seen: m.seen || m.isRead,
  error: m.error,
  senderWasDeleted: m.senderWasDeleted || false,
  sentAsChannel: m.sentAsChannel || false,
  digitalSignature: m.digitalSignature || false,
  signingMode: m.signingMode,
  signers: m.signers,
  /** Needed for superadmin OmmnioSign evidence export (signedAt per signer) */
  signatureRequests: m.signatureRequests,
  fields: m.fields,
  form: m.form,
  formVisibleAll: m.formVisibleAll,
  suggestions: m.suggestions ? [...m.suggestions] : m.suggestions,
  blocks: m.blocks,
  canForward: m.canForward,
  canEdit: m.canEdit,
  canDelete: m.canDelete,
  canPin: m.canPin,
  showForwarded: m.showForwarded,
  forwardedFrom: m.forwardedFrom,
  replyTo: m.replyTo
    ? {
        ...m.replyTo,
        channel_id: m.channel_id || m.channel,
      }
    : null,
  article: m.article?._id ? m.article : null,
  reactions: m.reactions,
  finalSignedDocUrl: m.finalSignedDocUrl,
});

const messagesReducer = (state = {}, { type, payload, meta }) => {
  switch (type) {
    case GET_MEMBER_CHANNELS_FULFILLED: {
      const lastMessages =
        payload.channels?.filter(c => c.lastMessage).map(c => c.lastMessage) || [];

      return {
        ...state,
        ...arrayToObject(lastMessages.map(c => createMessage(merge(state[c._id], c)))),
      };
    }

    case LOAD_MESSAGES_FULFILLED: {
      const existing = meta.replace ? omitBy(state, m => m.channel_id === meta.channel_id) : state;
      return {
        ...existing,
        ...arrayToObject(payload.messages.map(c => createMessage(merge(state[c._id], c)))),
      };
    }

    case GENERATE_MESSAGE_PREVIEW_FULFILLED: {
      const messages = { ...state };
      const message = messages[meta.message_id];

      if (isEmpty(payload) || !payload.title || !message) return messages;

      messages[meta.message_id] = merge(message, {
        previews: {
          ...message.previews,
          [meta.link]: {
            ...payload,
            link: meta.link,
            favicon: payload.image,
          },
        },
      });

      return messages;
    }

    case UPDATE_MESSAGE_FULFILLED:
      return {
        ...state,
        [meta.message_id]: createMessage({ ...state[meta.message_id], ...meta.message }),
      };

    case READ_MESSAGES_FULFILLED: {
      const markedAsSeenMessages = Object.values(state)
        .filter(m => m.channel_id === meta.channel_id && !m.isReadByMe)
        .map(m => ({
          ...m,
          seen: meta.authUser_id === m.from?._id ? m.seen : true,
          isReadByMe: true,
        }));

      return merge(state, arrayToObject(markedAsSeenMessages));
    }

    case SEND_MESSAGE_PENDING: {
      return merge(state, {
        [meta.message_id]: createMessage({
          ...meta.message,
          temp: true,
        }),
      });
    }

    case SEND_MESSAGE_REJECTED: {
      return merge(state, {
        [meta.message_id]: createMessage({
          ...state[meta.message_id],
          temp: false,
          error: true,
        }),
      });
    }

    case ADD_MESSAGE:
    case SEND_MESSAGE_FULFILLED: {
      const existing = state[payload._id];
      const merged = existing ? mergeMessagePatch(existing, payload) : payload;

      return {
        ...state,
        [payload._id]: createMessage({
          ...merged,
          temp: false,
          seen:
            state[payload._id]?.seen ||
            (merged.channel_id || merged.channel) === meta.watchedChannel_id ||
            merged.from?._id === meta.authUser_id,
        }),
      };
    }

    case CONFIRM_ACKNOWLEDGE_MESSAGE_FULFILLED: {
      const message = state[meta.message_id];
      if (!message) return state;

      const serverMessage =
        payload?.message ||
        (payload?._id === meta.message_id && Array.isArray(payload?.acknowledges) ? payload : null);

      if (serverMessage && Array.isArray(serverMessage.acknowledges)) {
        return {
          ...state,
          [meta.message_id]: createMessage(merge(message, serverMessage)),
        };
      }

      const newAcknowledge = {
        _id: uniqid(),
        user: meta.authUser_id,
        confirmedAt: Math.floor(Date.now() / 1000),
      };

      if (payload && typeof payload === 'object') {
        if (payload.signedDocUrl) newAcknowledge.signedDocUrl = payload.signedDocUrl;
        if (payload.logUrl) newAcknowledge.logUrl = payload.logUrl;
        if (payload.acknowledge && typeof payload.acknowledge === 'object') {
          Object.assign(newAcknowledge, payload.acknowledge);
        }
      }

      const prevAcks = message.acknowledges || [];
      const filtered = prevAcks.filter(a => String(a.user) !== String(meta.authUser_id));

      const newMessage = merge(message, {
        acknowledges: [...filtered, newAcknowledge],
      });

      return {
        ...state,
        [meta.message_id]: createMessage(newMessage),
      };
    }

    case MARK_AS_READ_MESSAGES: {
      const markedAsSeenMessages = Object.values(state)
        .filter(m => m.channel_id === payload && !m.seen)
        .map(m => ({ ...m, seen: true, isRead: true, isReadByMe: true }));

      return merge(state, arrayToObject(markedAsSeenMessages));
    }

    case DESTROY_MESSAGE_FULFILLED: {
      const messages = { ...state };
      if (payload.message.deletedAt) {
        delete messages[meta.message_id];
      } else {
        messages[meta.message_id] = createMessage({
          ...payload.message,
          temp: false,
          seen: false,
        });
      }
      return messages;
    }

    case ON_READ_MESSAGE: {
      const newMessages = payload.messages.reduce((acc, curr) => {
        const m = state[curr];
        if (m) {
          return {
            [curr]: {
              ...m,
              isRead: m.isRead || m.from._id !== payload.userId,
            },
          };
        }
        return acc;
      }, {});
      return merge(state, newMessages);
    }

    case PERMISSIONS_MESSAGE_PENDING: {
      return {
        ...state,
        [meta.message_id]: { ...state[meta.message_id], permissionsState: 'loading' },
      };
    }

    case PERMISSIONS_MESSAGE_REJECTED: {
      return {
        ...state,
        [meta.message_id]: { ...state[meta.message_id], permissionsState: 'failed' },
      };
    }

    case PERMISSIONS_MESSAGE_FULFILLED: {
      return {
        ...state,
        [meta.message_id]: { ...state[meta.message_id], ...payload, permissionsState: 'loaded' },
      };
    }

    default:
      return state;
  }
};

export default messagesReducer;
